import { useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"
import { 
  Home, 
  Server,
  ChevronRight,
  Folder,
  Leaf,
  Cylinder,
  Zap,
  Sparkles,
  FolderTree,
  Settings,
  Shield,
  Terminal,
  GitBranch,
  Box,
  Atom,
  Layers,
  Component,
  Palette,
  Rocket,
  FileCode,
  Database
} from "lucide-react"

interface NavItem {
  title: string
  href: string
  icon?: React.ReactNode
  children?: NavItem[]
}

const navigation: NavItem[] = [
  {
    title: "Home",
    href: "/",
    icon: <Home className="h-4 w-4" />,
  },
  {
    title: "NestJS Templates",
    href: "/nestjs",
    icon: <Server className="h-4 w-4" />,
    children: [
      {
        title: "MongoDB Template",
        href: "/nestjs/mongodb",
        icon: <Leaf className="h-4 w-4" />,
        children: [
          { 
            title: "Quick Start", 
            href: "/nestjs/mongodb/quick-start",
            icon: <Zap className="h-4 w-4" />
          },
          { 
            title: "Features", 
            href: "/nestjs/mongodb/features",
            icon: <Sparkles className="h-4 w-4" />
          },
          { 
            title: "Project Structure", 
            href: "/nestjs/mongodb/structure",
            icon: <FolderTree className="h-4 w-4" />
          },
          { 
            title: "Configuration", 
            href: "/nestjs/mongodb/configuration",
            icon: <Settings className="h-4 w-4" />
          },
          { 
            title: "Authentication", 
            href: "/nestjs/mongodb/authentication",
            icon: <Shield className="h-4 w-4" />
          },
          { 
            title: "Scripts", 
            href: "/nestjs/mongodb/scripts",
            icon: <Terminal className="h-4 w-4" />
          },
        ],
      },
      {
        title: "MySQL Template",
        href: "/nestjs/mysql",
        icon: <Cylinder className="h-4 w-4" />,
        children: [
          { 
            title: "Quick Start", 
            href: "/nestjs/mysql/quick-start",
            icon: <Zap className="h-4 w-4" />
          },
          { 
            title: "Features", 
            href: "/nestjs/mysql/features",
            icon: <Sparkles className="h-4 w-4" />
          },
          { 
            title: "Project Structure", 
            href: "/nestjs/mysql/structure",
            icon: <FolderTree className="h-4 w-4" />
          },
          { 
            title: "Migrations", 
            href: "/nestjs/mysql/migrations",
            icon: <GitBranch className="h-4 w-4" />
          },
          { 
            title: "Entities", 
            href: "/nestjs/mysql/entities",
            icon: <Box className="h-4 w-4" />
          },
          { 
            title: "Configuration", 
            href: "/nestjs/mysql/configuration",
            icon: <Settings className="h-4 w-4" />
          },
          { 
            title: "Scripts", 
            href: "/nestjs/mysql/scripts",
            icon: <Terminal className="h-4 w-4" />
          },
        ],
      },
    ],
  },
  {
    title: "React Templates",
    href: "/react",
    icon: <Atom className="h-4 w-4" />,
    children: [
      {
        title: "React + Shadcn",
        href: "/react/shadcn",
        icon: <Component className="h-4 w-4" />,
        children: [
          { title: "Quick Start", href: "/react/shadcn/quick-start", icon: <Zap className="h-4 w-4" /> },
          { title: "Features", href: "/react/shadcn/features", icon: <Sparkles className="h-4 w-4" /> },
          { title: "Architecture", href: "/react/shadcn/architecture", icon: <Layers className="h-4 w-4" /> },
          { title: "Components", href: "/react/shadcn/components", icon: <Box className="h-4 w-4" /> },
          { title: "Services", href: "/react/shadcn/services", icon: <GitBranch className="h-4 w-4" /> },
          { title: "State Management", href: "/react/shadcn/state", icon: <Settings className="h-4 w-4" /> },
          { title: "Theming", href: "/react/shadcn/theming", icon: <Palette className="h-4 w-4" /> },
        ],
      },
      {
        title: "React + Tailwind",
        href: "/react/tailwind",
        icon: <FileCode className="h-4 w-4" />,
        children: [
          { title: "Quick Start", href: "/react/tailwind/quick-start", icon: <Zap className="h-4 w-4" /> },
          { title: "Features", href: "/react/tailwind/features", icon: <Sparkles className="h-4 w-4" /> },
          { title: "Architecture", href: "/react/tailwind/architecture", icon: <Layers className="h-4 w-4" /> },
          { title: "Components", href: "/react/tailwind/components", icon: <Box className="h-4 w-4" /> },
          { title: "Services", href: "/react/tailwind/services", icon: <GitBranch className="h-4 w-4" /> },
          { title: "Theming", href: "/react/tailwind/theming", icon: <Palette className="h-4 w-4" /> },
        ],
      },
    ],
  },
  {
    title: "Next.js Templates",
    href: "/nextjs",
    icon: <Rocket className="h-4 w-4" />,
    children: [
      {
        title: "Next.js + Shadcn",
        href: "/nextjs/shadcn",
        icon: <Component className="h-4 w-4" />,
        children: [
          { title: "Quick Start", href: "/nextjs/shadcn/quick-start", icon: <Zap className="h-4 w-4" /> },
          { title: "Features", href: "/nextjs/shadcn/features", icon: <Sparkles className="h-4 w-4" /> },
          { title: "Architecture", href: "/nextjs/shadcn/architecture", icon: <Layers className="h-4 w-4" /> },
          { title: "Components", href: "/nextjs/shadcn/components", icon: <Box className="h-4 w-4" /> },
          { title: "Server & Client", href: "/nextjs/shadcn/server-client", icon: <Server className="h-4 w-4" /> },
          { title: "State Management", href: "/nextjs/shadcn/state", icon: <Database className="h-4 w-4" /> },
        ],
      },
      {
        title: "Next.js + Tailwind",
        href: "/nextjs/tailwind",
        icon: <FileCode className="h-4 w-4" />,
        children: [
          { title: "Quick Start", href: "/nextjs/tailwind/quick-start", icon: <Zap className="h-4 w-4" /> },
          { title: "Features", href: "/nextjs/tailwind/features", icon: <Sparkles className="h-4 w-4" /> },
          { title: "Architecture", href: "/nextjs/tailwind/architecture", icon: <Layers className="h-4 w-4" /> },
          { title: "Components", href: "/nextjs/tailwind/components", icon: <Box className="h-4 w-4" /> },
          { title: "Server & Client", href: "/nextjs/tailwind/server-client", icon: <Server className="h-4 w-4" /> },
          { title: "State Management", href: "/nextjs/tailwind/state", icon: <Database className="h-4 w-4" /> },
        ],
      },
    ],
  },
]

export function Sidebar() {
  const location = useLocation()

  return (
    <div className="flex h-full w-64 flex-col border-r bg-card">
      <div className="flex h-16 items-center border-b px-6">
        <Link to="/" className="flex items-center gap-2 font-semibold">
          <Folder className="h-6 w-6 text-primary" />
          <span className="text-lg">Templates</span>
        </Link>
      </div>
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {navigation.map((item) => (
            <NavItem 
              key={item.href} 
              item={item} 
              currentPath={location.pathname}
            />
          ))}
        </nav>
      </ScrollArea>
    </div>
  )
}

function NavItem({ 
  item, 
  currentPath,
  level = 0 
}: { 
  item: NavItem
  currentPath: string
  level?: number
}) {
  const isActive = currentPath === item.href
  const isParentActive = item.children?.some(child => currentPath.startsWith(child.href))
  const hasChildren = item.children && item.children.length > 0
  const [isOpen, setIsOpen] = useState(isActive || isParentActive)

  // Keep open if active or if any child is active
  useEffect(() => {
    if (isActive || isParentActive) {
      setIsOpen(true)
    }
  }, [isActive, isParentActive])

  return (
    <div>
      <Link
        to={item.href}
        onClick={(e) => {
          if (hasChildren) {
            e.preventDefault()
            setIsOpen(!isOpen)
          }
        }}
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all hover:bg-accent",
          isActive && "bg-primary/10 text-primary font-medium hover:bg-primary/20",
          !isActive && "text-muted-foreground hover:text-foreground"
        )}
        style={{ paddingLeft: `${level * 12 + 12}px` }}
      >
        {item.icon}
        <span>{item.title}</span>
        {hasChildren && (
          <ChevronRight className={cn("ml-auto h-4 w-4 transition-transform", isOpen && "rotate-90")} />
        )}
      </Link>
      
      {/* Render children */}
      {hasChildren && isOpen && (
        <div className="mt-1 space-y-1">
          {item.children!.map((child) => (
            <NavItem
              key={child.href}
              item={child}
              currentPath={currentPath}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

